/**
 * FinancialDashboardTab — section-driven financial analytics panel (India only).
 *
 * The `section` prop controls which content is rendered.
 * Tab navigation lives in CompanyDetail — no nested tabs here.
 *
 * Sections:
 *   overview  → KPI cards + Financial Summary table (quick snapshot)
 *   finance   → P&L Statement + Balance Sheet + Revenue→EV Sankey (year picker)
 *   revenue   → Revenue Trends + Capital Efficiency (WACC/ROIC/InvestedCapital/CAGR)
 *   ev        → EV Decomposition + EBITDA Analysis + EV Composition + Valuation Metrics + Key Insights
 *
 * Tab ↔ Section mapping mirrors the source Financial-Insight-Dashboard:
 *   Source "Summary" tab      → finance section  (statements + Sankey)
 *   Source "EV Insights" tab  → ev section       (all EV + EBITDA analysis + capital returns)
 *
 * Years state is persisted in Zustand (financialStore.selectedYearsRange)
 * so it survives tab switches without refetching.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Sparkles, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { useCompanyFinancials } from './hooks/useCompanyFinancials';
import { useEVInsights } from './hooks/useEVInsights';
import { useFinancialStore } from '../../stores/financialStore';
import MetricCardsRow from './sections/MetricCardsRow';
import ValuationMetricsTable from './sections/ValuationMetricsTable';
import ProfitLossTable from './sections/ProfitLossTable';
import BalanceSheetTable from './sections/BalanceSheetTable';
import CashFlowTable from './sections/CashFlowTable';
import AssumedInputsPanel from './sections/AssumedInputsPanel';
import RevenueTrendChart from './charts/RevenueTrendChart';
import EBITDAComparisonChart from './charts/EBITDAComparisonChart';
import EBITDAMarginTrendChartWithToggle from './charts/EBITDAMarginTrendChartWithToggle';
import EVBreakdownChart from './charts/EVBreakdownChart';

import EVTrendChart from './charts/EVTrendChart';
import WACCTrendChart from './charts/WACCTrendChart';
import ROICTrendChartWithToggle from './charts/ROICTrendChartWithToggle';
import InvestedCapitalTrendChart from './charts/InvestedCapitalTrendChart';
import CompetitorEVChart from './charts/CompetitorEVChart';
import DebtEquityChart from './charts/DebtEquityChart';
import ICCAGRChart from './charts/ICCAGRChart';
import CompetitorCAGRChart from './charts/CompetitorCAGRChart';
import CompetitorInvestedCapitalChart from './charts/CompetitorInvestedCapitalChart';
import EVBreakdownPercentageChart from './charts/EVBreakdownPercentageChart';
import EVBreakdownMultipleChart from './charts/EVBreakdownMultipleChart';
import ValuationSankeyChart from './charts/ValuationSankeyChart';
import SensitivitySection from './sections/SensitivitySection';
import CompanyComparisonTables from './sections/CompanyComparisonTables';
import MetricsComparisonSection from './sections/MetricsComparisonSection';
import SmartLoader from '../ui/SmartLoader';
import LoadingCard from '../ui/LoadingCard';
import NoDataState from '../ui/NoDataState';
import FinancialNoData from './FinancialNoData';
import { hasProfitLoss, hasBalanceSheet } from './utils/dataCompleteness';
import { FINANCIAL_LOAD_PHASES, FINANCIAL_LOAD_EXPECTED_SEC, buildPeerPhases } from './loaderPhases';
import { formatFiscalYear, formatCompactCurrency, formatPercent, formatUnitLabel } from './utils/formatters';
import { Card, CardHeader, CardBody } from '../ui/Card';
import type { UnifiedFinancialRecord } from '../../types/financial';

export type FinancialSection = 'overview' | 'revenue' | 'ev' | 'finance' | 'sensitivity' | 'competitors';

export type InsightItem = {
  insight_id: string;
  title: string;
  description: string;
  sentiment: string;
  action: string;
  confidence_score: number;
};

interface Props {
  companyName: string;
  section: FinancialSection;
  country?: string;
  companyInsights?: any;
  /** Opens the Manage Competitors modal — used by the competitors-section empty state. */
  onManageCompetitors?: () => void;
}

const YEARS_OPTIONS = [3, 5, 7, 10] as const;

// Card keys for the EV/EBITDA AI insights panel. Drives the default-expanded
// seed and the expand-all toggle, so both stay in step with the rendered cards.
const EV_INSIGHT_KEYS = ['ev_ebitda'] as const;

function SectionDivider({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 pt-4 pb-1">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-neutral-200 to-neutral-200" />
      <span className="text-xs font-bold text-neutral-400 uppercase tracking-widest px-1 shrink-0">{title}</span>
      <div className="h-px flex-1 bg-gradient-to-l from-transparent via-neutral-200 to-neutral-200" />
    </div>
  );
}

function FinancialSummaryTable({
  data,
  region,
}: {
  data: UnifiedFinancialRecord[];
  region: any;
}) {
  const unitLabel = formatUnitLabel(region);
  const rows = [
    { label: 'Revenue', key: 'total_revenue' as const, format: 'currency', bold: true },
    { label: 'EBITDA', key: 'ebitda' as const, format: 'currency', bold: true },
    { label: 'EBITDA Margin', key: 'ebitda_margin' as const, format: 'pct', indent: true },
    { label: 'EBIT', key: 'ebit' as const, format: 'currency' },
    { label: 'Net Profit', key: 'net_profit' as const, format: 'currency', bold: true },
    { label: 'EV', key: 'enterprise_value' as const, format: 'currency', bold: true },
    { label: 'EV / EBITDA', key: 'ev_ebitda' as const, format: 'multiple', indent: true },
    { label: 'ROIC', key: 'roic' as const, format: 'pct' },
    { label: 'WACC', key: 'wacc' as const, format: 'pct' },
    { label: 'Net Debt', key: 'net_debt' as const, format: 'currency' },
  ];

  const fmt = (val: number | null | undefined, format: string) => {
    if (val == null || isNaN(val)) return '—';
    if (format === 'currency') return formatCompactCurrency(val, region);
    if (format === 'pct') return formatPercent(val);
    if (format === 'multiple') return `${val.toFixed(1)}x`;
    return val.toFixed(2);
  };

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-neutral-800">Financial Summary</h3>
        {unitLabel && (
          <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 bg-neutral-100 border border-neutral-200 rounded px-2 py-1 whitespace-nowrap">
            {unitLabel}
          </span>
        )}
      </CardHeader>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                <th className="text-left py-2.5 px-3 text-neutral-500 font-semibold text-[11px] uppercase tracking-wider min-w-[12rem] sticky left-0 bg-neutral-50 z-10">
                  Metric
                </th>
                {/* Keyed by POSITION, not by `d.year`. A duplicate year in the
                    payload (two FinancialReports for one fiscal year, or a
                    cartesian row blow-up) would otherwise produce duplicate React
                    keys, which breaks reconciliation and leaves stale, repeated,
                    out-of-order columns in the DOM rather than simply rendering
                    the year twice. */}
                {data.map((d, i) => (
                  <th
                    key={`${d.year}-${i}`}
                    className="text-right py-2.5 px-3 text-neutral-700 font-bold text-[11px] whitespace-nowrap min-w-[5rem]"
                  >
                    {formatFiscalYear(d.year, region)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const rowBg = row.bold ? 'bg-neutral-50/80' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/30';
                const labelBg = row.bold ? 'bg-neutral-50/95' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/60';
                return (
                  <tr
                    key={row.key}
                    className={`border-b border-neutral-100 transition-colors hover:bg-blue-50/40 ${rowBg}`}
                  >
                    <td
                      className={`py-2 px-3 text-xs leading-snug sticky left-0 z-10 ${labelBg} ${
                        row.bold ? 'font-semibold text-neutral-800' : 'text-neutral-600'
                      } ${row.indent ? 'pl-7' : ''}`}
                    >
                      {row.label}
                    </td>
                    {data.map((d, i) => {
                      const val =
                        row.key === 'total_revenue'
                          ? d.total_revenue ?? d.revenue_from_operations
                          : (d[row.key] as number | null | undefined);
                      const isNeg = typeof val === 'number' && val < 0 && row.format === 'currency';
                      return (
                        <td
                          key={`${d.year}-${i}`}
                          className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${
                            isNeg ? 'text-red-600' : row.bold ? 'font-semibold text-neutral-900' : 'text-neutral-700'
                          } ${row.bold ? 'font-semibold' : ''}`}
                        >
                          {fmt(val, row.format)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}


export default function FinancialDashboardTab({ companyName, section, country, companyInsights, onManageCompetitors }: Props) {
  const years = useFinancialStore((s) => s.selectedYearsRange);
  const setYears = useFinancialStore((s) => s.setYearsRange);
  const setRegionConfig = useFinancialStore((s) => s.setRegionConfig);
  const compareWithCompetitors = useFinancialStore((s) => s.compareWithCompetitors);
  const competitorsList = useFinancialStore((s) => s.competitorsList);
  const competitorDataStore = useFinancialStore((s) => s.competitorData);
  const competitorsLoading = useFinancialStore((s) => s.competitorsLoading);
  const competitorsLoaded = useFinancialStore((s) => s.competitorsLoaded);
  const competitorsTotal = useFinancialStore((s) => s.competitorsTotal);
  const competitorsFailed = useFinancialStore((s) => s.competitorsFailed);

  // Sankey year picker — null means "use latestYear"
  const [sankeyYear, setSankeyYear] = useState<number | null>(null);
  // Year window offset for navigation (0 = most recent years, positive = older)
  const [windowOffset, setWindowOffset] = useState(0);
  // EV/EBITDA AI insights expanded state — Set allows multiple open simultaneously.
  // Seeded with every key so the insights read as visible on arrival.
  const [evInsightExpanded, setEvInsightExpanded] = useState<Set<string>>(
    () => new Set(EV_INSIGHT_KEYS)
  );
  const toggleEvInsight = (key: string) =>
    setEvInsightExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const allEvInsightsExpanded = evInsightExpanded.size === EV_INSIGHT_KEYS.length;
  const toggleAllEvInsights = () =>
    setEvInsightExpanded(allEvInsightsExpanded ? new Set() : new Set(EV_INSIGHT_KEYS));

  // Always fetch max data (10 years); windowing is done client-side for navigation
  const { data, isLoading, isError, error, fetchStatus } = useCompanyFinancials(companyName, 10, country);

  // Reset window when years selector or data changes
  useEffect(() => { setWindowOffset(0); }, [years, companyName]);

  // Fetch EV insights on the ev section (focal/self) and the competitors section
  // (peer). The peer list is passed ONLY for the competitors section, so ev
  // receives the standalone response (self_performance) and the competitors tab
  // receives analysis_type "competitive" (competitive_performance). The revenue
  // tab deliberately does NOT call this API and shows no insights card.
  const evInsightsCompetitors =
    section === 'competitors' && competitorsList.length > 0 ? competitorsList : undefined;
  const { data: evInsightsData, isLoading: evInsightsLoading } = useEVInsights(
    section === 'ev' || section === 'competitors' ? companyName : null,
    10,
    evInsightsCompetitors
  );

  useEffect(() => {
    if (data?.region) {
      setRegionConfig(data.region);
    }
  }, [data?.region, setRegionConfig]);

  // Auto-snap the year-range filter down when the company has fewer years
  // than the currently selected option (e.g. user had 10Y selected from a
  // previous company but the new one only has 5 years of data).
  // IMPORTANT: this hook must live ABOVE the early returns below so the
  // hook count is stable across `isLoading` → `loaded` transitions. Putting
  // it after the returns triggers React's "Rendered more hooks than during
  // the previous render" error and blanks the page on first successful load.
  const totalYearsForSnap = data?.financial_data?.length ?? 0;
  useEffect(() => {
    if (totalYearsForSnap > 0 && years > totalYearsForSnap) {
      const validOptions = YEARS_OPTIONS.filter((y) => y <= totalYearsForSnap);
      const fallback = validOptions[validOptions.length - 1] ?? totalYearsForSnap;
      setYears(fallback as typeof years);
    }
  }, [totalYearsForSnap, years, setYears]);

  if (isLoading) {
    return (
      <SmartLoader
        title="Loading financial data"
        subtitle={companyName}
        phases={FINANCIAL_LOAD_PHASES}
        progress={{ mode: 'elapsed', expectedSec: FINANCIAL_LOAD_EXPECTED_SEC }}
        active={isLoading}
        delayMs={250}
      />
    );
  }

  if (!data) {
    // React Query v5: a disabled query (enabled:!!companyName) is pending+idle
    // with no data. Render nothing in that idle state rather than a spurious
    // "unavailable" message.
    if (fetchStatus === 'idle' && !isError) return null;

    const status = (error as any)?.response?.status;
    const detail = (error as any)?.response?.data?.detail ?? (error as any)?.message ?? 'Unknown error';

    // A 404 means "not ingested yet" — a normal, expected state, not an error.
    if (status === 404) {
      return <FinancialNoData state="not-ingested" companyName={companyName} country={country} detail={detail} />;
    }

    // Real error (500/501/503/network) — keep a louder red alert.
    return (
      <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Financial data unavailable</p>
          <p className="text-xs text-red-700 mt-0.5">{detail}</p>
        </div>
      </div>
    );
  }

  const { financial_data, latest_data, cagr_metrics, region, company_name, sector, reporting_basis } = data;

  if (!financial_data || financial_data.length === 0) {
    return <FinancialNoData state="no-records" companyName={company_name} />;
  }

  // Both statements are required for any analysis, and the LATEST year is the
  // minimum bar — the KPI cards and every "current" figure key off it. If the
  // most recent year is missing either the P&L or the Balance Sheet, gate the
  // whole dashboard (every section below) with a self-explanatory message.
  // (financial_data is sorted ascending by year, so the last row is the latest.)
  const latestRecord = financial_data[financial_data.length - 1];
  const plOk = hasProfitLoss([latestRecord]);
  const bsOk = hasBalanceSheet([latestRecord]);
  if (!plOk || !bsOk) {
    const missing = !plOk && !bsOk ? 'both' : !bsOk ? 'balance-sheet' : 'profit-loss';
    return <FinancialNoData state="incomplete" missing={missing} companyName={company_name} />;
  }

  // The country calculator engines produce WACC/EV for PDF companies too
  // (book-value weights when there is no traded price, labelled in the
  // Assumptions panel) — so EV-dependent surfaces normally just work. This
  // gate only fires when NO year has any of these metrics (statements-only
  // ingestions, e.g. UK PDF), where Sankey/sensitivity would run on nothing.
  // Nulls are BY DESIGN: never treat as 0, never derive client-side.
  const hasValuationMetrics = financial_data.some(
    (d) => d.enterprise_value != null || d.market_cap != null || d.wacc != null
  );
  // Statements-only ingestions create NO CalculatedMetrics node at all —
  // the metrics (EV Insights) tab empty-states while Finance stays live.
  const hasCalculatedMetrics = financial_data.some(
    (d) =>
      d.roic != null ||
      d.nopat != null ||
      d.invested_capital != null ||
      d.wacc != null ||
      d.enterprise_value != null ||
      d.ev_ebitda != null
  );

  // Client-side year windowing.
  // The auto-snap effect for `years > totalYears` lives ABOVE the early
  // returns (see top of component) — moving it here would change the hook
  // count between renders and crash the dashboard.
  const totalYears = financial_data.length;

  const windowSize = Math.min(years, totalYears);
  // windowOffset=0 means show last `windowSize` years; positive means shift older
  const clampedOffset = Math.min(windowOffset, Math.max(0, totalYears - windowSize));
  const windowEnd = totalYears - clampedOffset;
  const windowStart = Math.max(0, windowEnd - windowSize);
  const displayData = financial_data.slice(windowStart, windowEnd);
  const canGoOlder = windowStart > 0;
  const canGoNewer = clampedOffset > 0;

  const latestYear = displayData[displayData.length - 1]?.year;
  const displaySankeyYear = sankeyYear ?? latestYear;

  // Year range label shown in navigation
  const firstDisplayYear = displayData[0]?.year;
  const lastDisplayYear = displayData[displayData.length - 1]?.year;

  // Build per-competitor records map. Only populated when the user has flipped
  // on the toggle AND at least one peer has loaded. Charts receive this
  // (possibly empty) record so they can render peer overlays.
  const competitorRecordsByName: Record<string, UnifiedFinancialRecord[]> =
    compareWithCompetitors
      ? Object.fromEntries(
          Object.entries(competitorDataStore).map(([name, resp]) => [
            name,
            resp.financial_data ?? [],
          ])
        )
      : {};
  // Only reveal the peer overlays once the ENTIRE fan-out has settled. Flipping
  // this true on the first peer to arrive rendered a misleading chart — e.g. a
  // "Peer Comparison" showing 2 bars while the other 4 competitors were still in
  // flight. Gating on `!competitorsLoading` keeps the loading placeholder up
  // until every peer has resolved (success or failure), so the chart appears
  // once with its full set instead of growing bar-by-bar.
  const showCompetitors =
    compareWithCompetitors &&
    !competitorsLoading &&
    Object.keys(competitorRecordsByName).length > 0;
  // True while peer financials are in flight but not yet rendered. Used to
  // swap in chart skeletons so the user sees loading feedback on the
  // chart cards themselves, not just the toggle.
  const peerChartsLoading =
    compareWithCompetitors &&
    competitorsList.length > 0 &&
    competitorsLoading &&
    !showCompetitors;

  // Single peer fan-out stepper with REAL {loaded}/{total} progress, shown once
  // at the top of the revenue/ev sections while peers load. The 8 chart sites get
  // a plain LoadingCard placeholder so eight duplicate steppers aren't rendered.
  // NOTE: derived with a plain map, deliberately NOT useMemo — this sits below the
  // early returns, so a hook here would change the hook count between the loading
  // and loaded renders and crash the dashboard (see the hazard note at the top of
  // this component).
  const peerTotal = competitorsTotal || competitorsList.length;
  const peerLoader = peerChartsLoading ? (
    <SmartLoader
      title="Loading peer financials"
      subtitle={`Fetching ${peerTotal} ${peerTotal === 1 ? 'company' : 'companies'} for comparison`}
      phases={buildPeerPhases(competitorsList, competitorDataStore, competitorsFailed)}
      progress={{ mode: 'count', completed: competitorsLoaded, total: peerTotal }}
      active={peerChartsLoading}
      delayMs={0}
    />
  ) : null;

  // Placeholder for an individual peer chart slot while the fan-out is in flight.
  const peerPlaceholder = (title: string, height = 300) => (
    <LoadingCard title={title} message="Loading peer comparison…" height={height} delayMs={0} />
  );

  // Metadata line reused across sections
  const metaLine = (
    <p className="text-xs text-neutral-500 flex items-center gap-1.5 flex-wrap">
      <span>
        {sector && `${sector} · `}
        {region.country} · {region.currency.unit} ({region.currency.code})
        {' · '}{totalYears} years available
      </span>
      {/* Which set of books these figures are. Consolidated and standalone differ
          substantially and MoneyControl's own page defaults to standalone, so
          without this there is no way to reconcile these numbers against it. */}
      {reporting_basis && (
        <Tooltip
          content={
            reporting_basis === 'consolidated'
              ? 'Figures include subsidiaries and joint ventures. The source site may default to its Standalone tab, which shows the parent company alone and will not match these numbers.'
              : 'Figures cover the parent company alone, excluding subsidiaries. Consolidated data was too short a series to analyse, so the full history is reported on this basis.'
          }
          position="auto"
          variant="light"
          maxWidth={320}
        >
          <span
            tabIndex={0}
            className="cursor-help capitalize px-1.5 py-0.5 rounded-full bg-neutral-100 border border-neutral-200 text-[10px] font-medium text-neutral-600"
          >
            {reporting_basis}
          </span>
        </Tooltip>
      )}
    </p>
  );

  // Standard years selector with navigation arrows
  const YearsSelector = (
    <div className="flex items-center justify-between mb-4 px-3 py-2.5 bg-neutral-50 rounded-xl border border-neutral-200">
      {metaLine}
      <div className="flex items-center gap-2">
        {/* Navigation arrows — only visible when more data exists than the window */}
        {totalYears > windowSize && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setWindowOffset(Math.min(totalYears - windowSize, clampedOffset + 1))}
              disabled={!canGoOlder}
              className="p-1 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Older years"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs text-neutral-500 tabular-nums min-w-[5rem] text-center">
              {firstDisplayYear && lastDisplayYear
                ? `FY${String(firstDisplayYear).slice(-2)}–FY${String(lastDisplayYear).slice(-2)}`
                : ''}
            </span>
            <button
              onClick={() => setWindowOffset(Math.max(0, clampedOffset - 1))}
              disabled={!canGoNewer}
              className="p-1 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Newer years"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-0.5 bg-white rounded-lg border border-neutral-200 p-0.5">
          {YEARS_OPTIONS.map((y) => {
            const exceedsAvailable = totalYears > 0 && y > totalYears;
            const isSelected = years === y;
            return (
              <button
                key={y}
                onClick={() => { if (!exceedsAvailable) { setYears(y); setWindowOffset(0); } }}
                disabled={exceedsAvailable}
                title={exceedsAvailable ? `Only ${totalYears} year${totalYears === 1 ? '' : 's'} of data available for this company` : undefined}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  isSelected
                    ? 'bg-primary-600 text-white shadow-sm'
                    : exceedsAvailable
                      ? 'text-neutral-300 cursor-not-allowed line-through'
                      : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {y}Y
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Year picker element passed as headerRight to the Sankey chart card
  const sankeyYearPicker = (
    <select
      value={displaySankeyYear}
      onChange={(e) => setSankeyYear(Number(e.target.value))}
      className="text-xs border border-neutral-200 rounded-md px-2.5 py-1.5 bg-white text-neutral-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
    >
      {(data.years_available ?? financial_data.map((d) => d.year)).map((y) => (
        <option key={y} value={y}>{formatFiscalYear(y, region)}</option>
      ))}
    </select>
  );

  // EV/EBITDA AI Insights card. `mode` selects which single commentary block to
  // show, so no tab shows both: 'focal' → self_performance (EV + Revenue tabs),
  // 'peer' → competitive_performance (Competitor Analysis tab). The peer block
  // only exists when useEVInsights was called with a competitor list.
  const renderEvInsightsCard = (mode: 'focal' | 'peer') => {
    const metrics = evInsightsData
      ? [
          { key: 'ev_ebitda' as const, label: 'EV / EBITDA Multiple', metric: evInsightsData.insights.ev_ebitda_insights },
        ]
          .map((m) => ({
            ...m,
            perf: mode === 'peer' ? m.metric.competitive_performance : m.metric.self_performance,
          }))
          .filter((m) => m.perf)
      : [];

    return (
      <>
        {/* ── EV/EBITDA Multiple — AI Insights ──────────────────────────── */}
        <SectionDivider title="EV/EBITDA Multiple — AI Insights" />
        <Card>
          <CardHeader className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <Sparkles className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-neutral-800">
                  EV/EBITDA Multiple — AI Insights
                </h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {mode === 'peer'
                    ? 'How the company compares with its peers on enterprise value'
                    : 'Generated commentary on enterprise value and multiple trends'}
                </p>
              </div>
            </div>

            {evInsightsData && metrics.length > 0 && (
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    mode === 'peer'
                      ? 'bg-violet-600 text-white border border-violet-600'
                      : 'bg-violet-50 text-violet-700 border border-violet-100'
                  }`}
                >
                  {mode === 'peer' ? (
                    <>
                      <Users className="w-3 h-3" />
                      Peer-relative
                    </>
                  ) : (
                    'Standalone'
                  )}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-xs font-medium text-neutral-500">
                  {evInsightsData.years_analyzed}Y
                </span>
                <button
                  onClick={toggleAllEvInsights}
                  className="px-2 py-1 rounded-md text-xs font-medium text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 transition-colors"
                >
                  {allEvInsightsExpanded ? 'Collapse all' : 'Expand all'}
                </button>
              </div>
            )}
          </CardHeader>

          <CardBody>
            {evInsightsLoading ? (
              <LoadingCard
                asCard={false}
                message="Generating EV insights…"
                height={140}
                delayMs={0}
              />
            ) : metrics.length > 0 ? (
              <div className="space-y-3">
                {metrics.map(({ key, label, metric, perf }) => {
                  const expanded = evInsightExpanded.has(key);
                  return (
                    <div
                      key={key}
                      className={`rounded-lg border overflow-hidden transition-colors ${
                        expanded ? 'border-violet-200' : 'border-neutral-200'
                      }`}
                    >
                      <button
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors text-left"
                        onClick={() => toggleEvInsight(key)}
                        aria-expanded={expanded}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            className={`w-1 h-6 rounded-full shrink-0 transition-colors ${
                              expanded ? 'bg-violet-400' : 'bg-neutral-200'
                            }`}
                          />
                          <span className="text-sm font-semibold text-neutral-800 truncate">{label}</span>
                          <span className="text-xs text-neutral-400 truncate">{metric.metric_name}</span>
                        </div>
                        {expanded ? (
                          <ChevronUp className="w-4 h-4 text-neutral-400 shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-neutral-400 shrink-0" />
                        )}
                      </button>

                      {expanded && perf && (
                        <div className="px-4 pb-4 border-t border-neutral-100">
                          <div className="pt-3">
                            <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                              {perf.title}
                            </h4>
                            <p className="text-sm text-neutral-700 leading-relaxed mb-3">
                              {perf.summary}
                            </p>
                            <ul className="space-y-1.5 mb-3">
                              {perf.key_points.map((pt, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-neutral-600">
                                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 mt-1.5 shrink-0" />
                                  {pt}
                                </li>
                              ))}
                            </ul>
                            <div className="bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
                              <p className="text-xs font-medium text-violet-800">{perf.verdict}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <p className="text-xs text-neutral-400 text-right">
                  Analysis by {evInsightsData?.company} · {evInsightsData?.years_analyzed} years
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 py-8 text-center border border-dashed border-neutral-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-neutral-300" />
                <span className="text-sm text-neutral-400">
                  {mode === 'peer'
                    ? 'Peer insights not available — ensure competitor financials are ingested'
                    : 'AI insights not available — ensure financial data is ingested'}
                </span>
              </div>
            )}
          </CardBody>
        </Card>
      </>
    );
  };

  // ── Overview ───────────────────────────────────────────────────────────────
  if (section === 'overview') {
    return (
      <div className="space-y-4">
        {YearsSelector}
        <FinancialSummaryTable data={displayData} region={region} />
      </div>
    );
  }

  // ── Finance — Statements + Sankey (year picker on the chart card itself) ──
  if (section === 'finance') {
    return (
      <div className="space-y-4">
        {YearsSelector}
        <ProfitLossTable data={displayData} region={region} />
        <BalanceSheetTable data={displayData} region={region} />
        {/* Present for PDF-ingested companies only — self-hides otherwise. */}
        <CashFlowTable data={displayData} region={region} />
        {hasValuationMetrics ? (
          <ValuationSankeyChart
            companyName={company_name}
            year={displaySankeyYear}
            region={region}
            headerRight={sankeyYearPicker}
          />
        ) : (
          <NoDataState
            cardTitle="Revenue → EV Flow"
            title="Enterprise value not calculated"
            hint="The Revenue → EV flow needs an enterprise value, and none was computed for this company — its ingestion stored financial statements only. Statement figures above are unaffected."
          />
        )}
      </div>
    );
  }

  // ── Sensitivity Analysis ──────────────────────────────────────────────────
  // Tornado chart + EV heatmap — triggered on-demand via "Run Analysis" button.
  // Works for PDF companies: without a traded market price the baseline uses
  // book-value assumptions, which the Assumptions panel above it spells out.
  if (section === 'sensitivity') {
    // Scenarios flex WACC, CAP and EV — meaningless when none were computed
    // (statements-only ingestions).
    if (!hasValuationMetrics) {
      return (
        <div className="space-y-4">
          <NoDataState
            title="Sensitivity analysis unavailable"
            hint="Sensitivity scenarios vary WACC, CAP and EV, and none were computed for this company — its ingestion stored financial statements only. The statements are on the Finance tab."
          />
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <AssumedInputsPanel data={financial_data} />
        <SensitivitySection companyName={company_name} region={region} />
      </div>
    );
  }

  // ── Revenue & Capital ──────────────────────────────────────────────────────
  // Revenue Trends + Capital Efficiency (WACC / ROIC / Invested Capital / CAGR)
  if (section === 'revenue') {
    // Focal-company only — all peer comparisons now live on the Competitors tab.
    return (
      <div className="space-y-4">
        {YearsSelector}

        <SectionDivider title="Revenue Trend" />
        <RevenueTrendChart data={displayData} region={region} />

        <SectionDivider title="Invested Capital" />
        <InvestedCapitalTrendChart data={displayData} region={region} />

        <SectionDivider title="Capital Efficiency" />
        <ROICTrendChartWithToggle data={displayData} region={region} />
        <WACCTrendChart data={displayData} region={region} />

        <SectionDivider title="Capital Structure" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DebtEquityChart data={displayData} region={region} />
          <ICCAGRChart data={financial_data} region={region} />
        </div>

      </div>
    );
  }

  // ── Competitors ────────────────────────────────────────────────────────────
  // All peer comparisons, consolidated into one tab. Reached only when the
  // "Compare with competitors" toggle is on (the tab is otherwise hidden), so
  // the peer charts always render in peer mode.
  if (section === 'competitors') {
    // No peers configured yet — prompt the user to add some via the Manage modal.
    if (competitorsList.length === 0) {
      return (
        <div className="space-y-4">
          {YearsSelector}
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center border border-dashed border-neutral-200 rounded-xl">
            <Users className="w-8 h-8 text-neutral-300" />
            <div>
              <p className="text-sm font-semibold text-neutral-700">No competitors to compare yet</p>
              <p className="text-xs text-neutral-500 mt-1">
                Add peer companies to see side-by-side comparisons across EV, EBITDA, ROIC and more.
              </p>
            </div>
            {onManageCompetitors && (
              <button
                onClick={onManageCompetitors}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Manage Competitors
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {YearsSelector}
        {peerLoader}

        {/* Peer (competitor) EV/EBITDA AI commentary only — focal commentary lives on EV/Revenue. */}
        {renderEvInsightsCard('peer')}

        {/* Key metrics vs peer-average — cards + comparison table (Revenue CAGR,
            EBITDA %, ROIC, WACC). Self-contained: fetches its own comparison data. */}
        <SectionDivider title="Key Metrics — Company vs Peers" />
        <MetricsComparisonSection companyName={company_name} competitors={competitorsList} />

        <SectionDivider title="EV/EBITDA Multiple — Peer Comparison" />
        {peerChartsLoading ? (
          peerPlaceholder('EV/EBITDA Multiple — Peer Comparison (x)', 300)
        ) : (
          <CompetitorEVChart
            data={displayData}
            region={region}
            companyName={company_name}
            competitorData={competitorRecordsByName}
          />
        )}

        <SectionDivider title="EV Composition — Peer Comparison" />
        {showCompetitors ? (
          <>
            <EVBreakdownMultipleChart
              data={financial_data}
              companyName={company_name}
              competitorData={competitorRecordsByName}
              region={region}
            />
            <EVBreakdownPercentageChart
              data={displayData}
              companyName={company_name}
              competitorData={competitorRecordsByName}
              region={region}
            />
          </>
        ) : peerChartsLoading ? (
          peerPlaceholder('EV Composition — Peer Comparison', 320)
        ) : null}

        <SectionDivider title="Revenue CAGR — Peer Comparison" />
        {showCompetitors ? (
          <CompetitorCAGRChart
            data={financial_data}
            companyName={company_name}
            competitorData={competitorRecordsByName}
            region={region}
            years={years}
          />
        ) : peerChartsLoading ? (
          peerPlaceholder('Revenue CAGR — Peer Comparison', 320)
        ) : null}

        <SectionDivider title="EBITDA Margin — Peer Comparison" />
        {peerChartsLoading ? (
          peerPlaceholder('10-Year EBITDA Margin Trend — Multi-Company Comparison (%)', 320)
        ) : (
          <EBITDAMarginTrendChartWithToggle
            data={financial_data}
            region={region}
            companyName={company_name}
            competitorData={competitorRecordsByName}
          />
        )}

        <SectionDivider title="Capital Efficiency — Peer Comparison" />
        {peerChartsLoading ? (
          peerPlaceholder('10-Year ROIC Trend — Multi-Company Comparison (%)', 320)
        ) : (
          <ROICTrendChartWithToggle
            data={financial_data}
            region={region}
            companyName={company_name}
            competitorData={competitorRecordsByName}
          />
        )}
        {peerChartsLoading ? (
          peerPlaceholder('WACC — Peer Comparison (%)', 300)
        ) : (
          <WACCTrendChart
            data={displayData}
            region={region}
            companyName={company_name}
            competitorData={competitorRecordsByName}
          />
        )}

        <SectionDivider title="Invested Capital CAGR — Peer Comparison" />
        {showCompetitors ? (
          <CompetitorInvestedCapitalChart
            data={financial_data}
            companyName={company_name}
            competitorData={competitorRecordsByName}
            region={region}
            years={years}
          />
        ) : peerChartsLoading ? (
          peerPlaceholder('Invested Capital CAGR — Peer Comparison', 320)
        ) : null}

        <SectionDivider title="Capital Structure — Peer Comparison" />
        {peerChartsLoading ? (
          peerPlaceholder('Debt/Equity — Peer Comparison', 300)
        ) : (
          <DebtEquityChart
            data={displayData}
            region={region}
            companyName={company_name}
            competitorData={competitorRecordsByName}
          />
        )}
      </div>
    );
  }

  // ── EV Insights ───────────────────────────────────────────────────────────
  // 1. EV/EBITDA Multiple — AI Insights (lead section, expanded by default)
  // 2. EV/EBITDA Trend
  // 3. EV Decomposition (Breakdown) + EV Composition Analysis
  // 4. EBITDA Analysis
  // 5. Valuation & Return Metrics table
  // 6. 10-Year Performance Evaluation (from companyInsights)

  // UK PDF ingestions have statements but no CalculatedMetrics node at all —
  // empty-state the whole metrics view instead of a page of blank charts.
  if (!hasCalculatedMetrics) {
    return (
      <div className="space-y-4">
        {YearsSelector}
        <NoDataState
          title="Calculated metrics unavailable"
          hint={`No calculated metrics exist for ${company_name} — its PDF ingestion stored financial statements only. The Profit & Loss and Balance Sheet are available on the Finance tab.`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {YearsSelector}

      {/* Engine assumptions for PDF-extracted statements (which inputs came
          from official sources vs the PDF, which defaults applied, which
          market-derived metrics were suppressed). Self-hides for scraped
          companies, which carry no Assumed_Inputs. */}
      <AssumedInputsPanel data={financial_data} />

      {/* Focal (self) EV/EBITDA AI insights only — peer commentary lives on the Competitor Analysis tab. */}
      {renderEvInsightsCard('focal')}

      {/* ── 2: EV/EBITDA Trend ─────────────────────────────────── */}
      <SectionDivider title="EV/EBITDA Trend" />
      <EVTrendChart data={displayData} region={region} />
      <CompetitorEVChart
        data={displayData}
        region={region}
        companyName={company_name}
      />

      {/* ── 3: Enterprise Value Breakdown: Current vs Future Growth ──── */}
      <SectionDivider title="Enterprise Value Breakdown" />
      <EVBreakdownChart data={displayData} region={region} />

      {/* ── 4: EBITDA Analysis ───────────────────────────────────────────── */}
      <SectionDivider title="EBITDA Analysis" />
      <EBITDAComparisonChart data={displayData} region={region} />
      <EBITDAMarginTrendChartWithToggle data={displayData} region={region} />

      {/* ── 5: Valuation & Return Metrics ────────────────────────────────── */}
      <SectionDivider title="Valuation & Return Metrics" />
      <ValuationMetricsTable data={displayData} region={region} />

      {/* ── 5b: Multi-Year Financial Summary (tabbed: Detailed + Valuation) */}
      <CompanyComparisonTables data={financial_data} region={region} />

      {/* ── 6: 10-Year Performance Evaluation ────────────────────────────── */}
      {companyInsights?.insights && (
        <>
          <SectionDivider title="10-Year Performance Evaluation" />
          <div className="space-y-4">
            {([
              { key: 'revenue_commentary', color: 'border-l-blue-400' },
              { key: 'ebitda_commentary', color: 'border-l-green-400' },
              { key: 'asset_efficiency_commentary', color: 'border-l-violet-400' },
            ] as const).map(({ key, color }) => {
              const commentary = companyInsights.insights[key];
              if (!commentary) return null;
              return (
                <div key={key} className={`bg-white rounded-lg border border-neutral-200 border-l-4 ${color} p-4`}>
                  <h3 className="font-semibold text-neutral-900 mb-2 text-sm">{commentary.metric_name}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-3">
                    {commentary.self_performance.summary}
                  </p>
                  <ul className="space-y-1 mb-3">
                    {commentary.self_performance.key_points.map((pt: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-neutral-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 mt-1 shrink-0" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <div className="bg-neutral-50 rounded-md px-3 py-2">
                    <p className="text-xs font-medium text-neutral-700">{commentary.self_performance.verdict}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
